import { IImageLibraryDto, IOpenAIGeneratedImagesDTO } from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import { Component, KeyboardEvent, createRef } from "react";
import { OpenAIService } from "../../../../../services/openai-service";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSmallSpinner from "../../../../ui-kit/kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { failureMessageOr, toastService } from "../../../../ui-kit/kuika-toast/toast-service";
import KuikaSmallMediumText from "../../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import "./styling-color-picker.scss";

enum EImageGeneratorMessageUserType {
  User = 0,
  AI = 1
}

enum EImageStatus {
  Idle,
  Uploading,
  Uploaded
}

interface IUploadQueueItem {
  messageIndex: number;
  imageIndex: number;
}

interface IGeneratedImage extends IImageLibraryDto {
  status: EImageStatus;
}

interface IImageGeneratorMessage {
  userType: EImageGeneratorMessageUserType;
  text?: string;
  generatedImages?: IGeneratedImage[];
}

interface IAIImageGeneratorState {
  uploadQueue: IUploadQueueItem[];
  isAIGeneratingImages: boolean;
  messages: IImageGeneratorMessage[];
  userMessage: string;
}

interface IAIImageGeneratorProps {
  uploadImage: (
    image: IImageLibraryDto[],
    cb?: {
      onSuccess?: () => void;
      onError?: () => void;
      onFinish?: () => void;
    }
  ) => void;
  isUploaderBusy: boolean;
}

export class AIImageGenerator extends Component<IAIImageGeneratorProps, IAIImageGeneratorState> {
  messagesContainerRef = createRef<HTMLDivElement>();

  constructor(props: IAIImageGeneratorProps) {
    super(props);
    this.state = {
      uploadQueue: [],
      isAIGeneratingImages: false,
      messages: [
        {
          userType: EImageGeneratorMessageUserType.AI,
          text: "Please describe the image you need from me!"
        }
      ],
      userMessage: ""
    };
  }

  componentDidUpdate(): void {
    if (this.state.uploadQueue.length > 0 && !this.props.isUploaderBusy) {
      this.arrangeUploadQueue();
    }
  }

  arrangeUploadQueue() {
    const queue = [...this.state.uploadQueue];
    this.setState({ uploadQueue: [] }, () => {
      this.handleUploadImage(queue);
    });
  }

  handleChangeImageStatus(messageIndex: number, imageIndex: number, status: EImageStatus) {
    const messages = [...this.state.messages];
    const msg = messages[messageIndex];
    if (!msg) return;
    if (!msg.generatedImages) return;

    const img = msg.generatedImages[imageIndex];
    img.status = status;
    this.setState({ messages });
  }

  handlePushToUploadQueue = (messageIndex: number, imageIndex: number) => {
    const uploadQueue = [...this.state.uploadQueue];

    if (uploadQueue.some((queueItem) => queueItem.imageIndex === imageIndex && queueItem.messageIndex === messageIndex))
      return;

    uploadQueue.push({ messageIndex, imageIndex });
    this.setState({ uploadQueue }, () => {
      this.handleChangeImageStatus(messageIndex, imageIndex, EImageStatus.Uploading);
    });
  };

  handleUploadImage = async (queue: IUploadQueueItem[]) => {
    const imagesToUpload: IImageLibraryDto[] = [];
    queue.forEach(({ imageIndex, messageIndex }) => {
      const messages = [...this.state.messages];
      const msg = messages[messageIndex];
      if (!msg) return;
      if (!msg.generatedImages) return;

      const img = msg.generatedImages[imageIndex];
      const image = { fileName: img.fileName, content: img.content } as IImageLibraryDto;
      imagesToUpload.push(image);
    });
    this.props.uploadImage(imagesToUpload, {
      onSuccess: () => {
        queue.forEach(({ messageIndex, imageIndex }) => {
          this.handleChangeImageStatus(messageIndex, imageIndex, EImageStatus.Uploaded);
        });
      },
      onError: () => {
        queue.forEach(({ messageIndex, imageIndex }) => {
          this.handleChangeImageStatus(messageIndex, imageIndex, EImageStatus.Idle);
        });
      }
    });
  };

  handleSendImageGenerateRequest = () => {
    let prompt = this.state.userMessage;

    if (prompt) {
      this.handlePushMessage({ userType: EImageGeneratorMessageUserType.User, text: prompt });
    } else {
      const userMessages = this.state.messages.filter((msg) => msg.userType === EImageGeneratorMessageUserType.User);
      if (userMessages.length === 0) return;

      prompt = userMessages[userMessages.length - 1].text as string;
    }

    this.setState({ userMessage: "", isAIGeneratingImages: true });

    const generation: Promise<AxiosResponse<IOpenAIGeneratedImagesDTO>> = OpenAIService.GenerateImage({
      prompt,
      size: "1024x1024",
      numberOfImage: 1
    });
    // Generating takes a while, and the picker can be closed meanwhile, so one toast carries it from
    // "Generating" to its outcome. The ring beside the prompt stays as it was.
    toastService.promise(generation, {
      loading: "Generating the image…",
      success: "Image generated",
      error: failureMessageOr("The image could not be generated.")
    });
    generation
      .then(({ data: { images } }) => {
        const robotMessage: IImageGeneratorMessage = {
          userType: EImageGeneratorMessageUserType.AI,
          generatedImages: images.map(
            (content, i) =>
              ({
                fileName: `${Date.now()}_${i}.png`,
                content,
                status: EImageStatus.Idle
              }) as IGeneratedImage
          )
        };
        this.handlePushMessage(robotMessage);
      })
      .catch((): void => {
        // The toast has said why.
      })
      .finally(() => {
        this.setState({ isAIGeneratingImages: false });
      });
  };

  handleInputKeydown = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.code === "Enter" && this.state.userMessage) {
      e.preventDefault();
      this.handleSendImageGenerateRequest();
    }
  };

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ userMessage: e.target.value });
  };

  handlePushMessage = (message: IImageGeneratorMessage) => {
    const messages = this.state.messages;
    messages.push(message);
    this.setState({ messages }, () => {
      const lastMessage =
        this.messagesContainerRef.current?.children[this.messagesContainerRef.current.children.length - 1];
      lastMessage?.scrollIntoView({ behavior: "smooth", block: "end" });
    });
  };

  renderMessage = (message: IImageGeneratorMessage, messageIndex: number) => {
    return (
      <div
        className={`kuika_custom-color-picker__message ${
          message.userType === EImageGeneratorMessageUserType.AI ? "ai" : "user"
        }`}
      >
        <div className="kuika_custom-color-picker__message-avatar">
          {message.userType === EImageGeneratorMessageUserType.AI ? (
            <img src="/images/common/ai-bot.svg" alt="AI" />
          ) : (
            <KuikaSvg name="user" />
          )}
        </div>
        <div className="kuika_custom-color-picker__message-content">
          {message.text && <KuikaText>{message.text}</KuikaText>}
          {message.generatedImages &&
            message.generatedImages.map((img, imageIndex) => (
              <div key={imageIndex} className="kuika_custom-color-picker__generated-image">
                <img width={200} src={img.content} alt="Generated" />
                <div
                  data-testid="fillAreaGenerateImageWithAiDownloadImageButton"
                  className="kuika_custom-color-picker__image-actions"
                >
                  {img.status === EImageStatus.Uploaded ? (
                    <KuikaSvg name="check" className="kuika_custom-color-picker__image-actions--check" />
                  ) : img.status === EImageStatus.Uploading ? (
                    <KuikaSmallSpinner />
                  ) : (
                    <KuikaSvg name="download" onClick={() => this.handlePushToUploadQueue(messageIndex, imageIndex)} />
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
    );
  };

  render() {
    return (
      <div className="kuika_custom-color-picker__ai-container">
        <div className="kuika_custom-color-picker__messages-container">
          <div className="kuika_custom-color-picker__messages" ref={this.messagesContainerRef}>
            {this.state.messages.map((message, i) => this.renderMessage(message, i))}
          </div>
          {this.state.messages.some((msg) => msg.generatedImages) && !this.state.isAIGeneratingImages && (
            <div className="kuika_custom-color-picker__actions">
              <KuikaText>More alternatives?</KuikaText>

              <KuikaButton
                type="secondary"
                color="default"
                size="sm"
                onClick={() => this.handleSendImageGenerateRequest()}
                disabled={this.state.isAIGeneratingImages}
              >
                <KuikaSvg name="sparkles" type="datasource-icons" />
                <KuikaSmallMediumText>RETRY</KuikaSmallMediumText>
              </KuikaButton>
            </div>
          )}
        </div>
        <div className="kuika_custom-color-picker__input-wrapper">
          <KuikaInput
            data-testid="fillAreaGenerateImageWithAiPromptInput"
            placeholder="Type your prompt here..."
            onKeyDown={this.handleInputKeydown}
            onChange={this.handleInputChange}
            value={this.state.userMessage}
            size="lg"
            disabled={this.state.isAIGeneratingImages}
            rightIcon={
              this.state.isAIGeneratingImages ? (
                <KuikaSmallSpinner />
              ) : (
                <KuikaSvg name="arrow-up" type="arrows" onClick={this.handleSendImageGenerateRequest} />
              )
            }
          />
        </div>
      </div>
    );
  }
}
